// Loom's proposal in Focus Chat — the `draft` rows the agent attached to its open
// story fields and lists, pinned above the composer, each one click from being
// the user's own answer.
//
// The chat shows no card: no badges, no DoR, no Accept. But the agent calls
// `propose_card` here too, with a `draft` on each open field, and the
// `readiness_card` event reaches the browser — until this block, Focus Chat
// dropped it and the agent wrote its proposals into its reply as prose with
// "send me back the lines you want". Users sent back "passt so" and "I confirm"
// instead, which name nothing and ground nothing.
//
// Each row is Loom's text with the same pair every other surface shows: "Use
// this text" (the user's answer, in one click) and "Write my own" (the only
// thing that opens a box — here, the chat composer, with the field's name line
// in it). A list gets "Use this list" / "Change the list". Clicks are HELD and go
// out together as one ordinary chat message (see FocusChat and `chatAnswer.ts`).
//
// Drafts only. A red value is never offered here: the chat directive describes
// drafts, and in a chat the agent repairs a red by asking again with a draft.
// Priority and milestone stay in the agent's prose.

import type { JSX } from "react";

import type { SlotVerdict } from "@loom/shared";
import { isStoryAnswerField } from "@loom/shared/readiness";

import { composerNamesField } from "../chat/render/chatAnswer";
import {
  isListField,
  loomAnswerOf,
  loomAnswerText,
  useArmedClick,
} from "../chat/render/loomAnswer";
import { useT, usePlural } from "../i18n/index.js";
import { reportClientEvent } from "../lib/client-trace";

import { FIELD_ORDER, useFieldCopy } from "./copy";
import { LoomBlock, UseButton } from "./widgets";

/** One field Loom has a proposal for. */
export interface ChatProposalRow {
  readonly field: string;
  readonly kind: "text" | "list";
  /** Prose: Loom's one row. */
  readonly text?: string;
  /** List: Loom's rows, shown numbered and read-only. */
  readonly rows?: ReadonlyArray<string>;
  /** The exact bytes a click sends (a list: its `buildListAnswer` text). A story
   *  answer's name line is added when the batch is composed, not here. */
  readonly sendText: string;
}

/**
 * The fields the chat shows a proposal for: the three story clauses and the
 * acceptance criteria — the four the chat directive tells the agent it may
 * draft. The Definition of Done is the agent's to decide (it is a "derive"
 * field), so its draft is never put to the user as theirs to take.
 */
const isChatProposalField = (field: string): boolean =>
  isStoryAnswerField(field) || (field === "acceptance_criteria" && isListField(field));

/**
 * The rows the block shows for one card: in the card's field order, every
 * proposal field that is still `needs_input`, not skipped, and whose `draft` is
 * an answer by the one rule every surface reads (`loomAnswerOf`) — a story
 * field's single row, or the criteria's rows. Never settled, so a red is never
 * offered; never a choice.
 */
export const chatProposalRows = (
  verdicts: ReadonlyArray<SlotVerdict>,
): ChatProposalRow[] => {
  const out: ChatProposalRow[] = [];
  for (const field of FIELD_ORDER) {
    if (!isChatProposalField(field)) continue;
    const v = verdicts.find((x) => x.field === field);
    if (v === undefined || v.verdict !== "needs_input" || v.declined === true) continue;
    const answer = loomAnswerOf(
      field,
      {
        verdict: v.verdict,
        ...(typeof v.value === "string" ? { value: v.value } : {}),
        ...(v.draft !== undefined ? { draft: v.draft } : {}),
      },
      { settled: false },
    );
    if (answer === null || answer.source !== "draft") continue;
    if (answer.kind === "text") {
      out.push({ field, kind: "text", text: answer.text, sendText: answer.text });
    } else if (answer.kind === "list") {
      out.push({
        field,
        kind: "list",
        rows: answer.rows,
        sendText: loomAnswerText(field, answer),
      });
    }
  }
  return out;
};

const trace = (control: string): void =>
  reportClientEvent("ui.click", { control, surface: "focus_chat" });

/** A row whose answer is held: the text, muted, the promise, and Undo — armed
 *  against the second click of the accept that put it here. */
function HeldLine({
  text,
  label,
  onUndo,
}: {
  readonly text: string;
  /** The field's name, for the Undo's accessible name. */
  readonly label: string;
  readonly onUndo: () => void;
}) {
  const t = useT();
  const armed = useArmedClick();
  return (
    <div className="focus-proposal-held">
      <p className="focus-proposal-heldtext">{text}</p>
      <div className="focus-proposal-status">
        <span>{t("readiness.answerHeld")}</span>
        <button
          type="button"
          className="focus-btn is-ghost"
          aria-label={t("readiness.undoAria", { label })}
          onClick={(e) => {
            if (!armed(e)) return;
            onUndo();
          }}
        >
          {t("readiness.undo")}
        </button>
      </div>
    </div>
  );
}

export function ChatProposalBlock({
  issueIid,
  rows,
  held,
  sent,
  composer,
  composerLocked = false,
  paused,
  busy,
  heldCount,
  canSendNow,
  onUse,
  onUndo,
  onWriteOwn,
  onChangeList,
  onSendNow,
}: {
  readonly issueIid: number;
  readonly rows: ReadonlyArray<ChatProposalRow>;
  /** Held answers for this issue, by field (the bytes that will go out). */
  readonly held: ReadonlyMap<string, string>;
  /** Fields whose answer went out and that Loom has not read yet. */
  readonly sent: ReadonlySet<string>;
  /** The composer's text — a row whose name line is in it is being written
   *  there, and offers no accept. */
  readonly composer: string;
  /** The composer is holding a message that is on its way: nothing may be
   *  put into it until that lands. */
  readonly composerLocked?: boolean;
  /** The chat is paused or its workspace stopped: nothing goes out on its own. */
  readonly paused: boolean;
  readonly busy: boolean;
  /** Everything held, across issues. */
  readonly heldCount: number;
  /** "Send now" can put the held answers on their way right now. */
  readonly canSendNow: boolean;
  readonly onUse: (field: string, text: string) => void;
  readonly onUndo: (field: string) => void;
  readonly onWriteOwn: (field: string) => void;
  readonly onChangeList: (field: string, text: string) => void;
  readonly onSendNow: () => void;
}): JSX.Element | null {
  const t = useT();
  const tn = usePlural();
  const fieldCopy = useFieldCopy();
  // A held answer whose row is gone (Loom re-proposed without a draft) still
  // shows, so its Undo does not vanish while the bytes are waiting to go out.
  const shown = FIELD_ORDER.filter(
    (f) => rows.some((r) => r.field === f) || held.has(f) || sent.has(f),
  );
  const anyOpen = rows.length > 0 || held.size > 0;
  if (!anyOpen || shown.length === 0) return null;
  return (
    <section
      className="focus-proposal"
      aria-label={t("focus.chat.proposal.title", { iid: issueIid })}
    >
      <p className="focus-proposal-title">
        {t("focus.chat.proposal.title", { iid: issueIid })}
      </p>
      <p className="focus-proposal-help">{t("focus.chat.proposal.help")}</p>
      <div className="focus-proposal-rows">
        {shown.map((field) => {
          const row = rows.find((r) => r.field === field);
          const label = fieldCopy(field).label;
          const heldText = held.get(field);
          let body: JSX.Element | null;
          if (heldText !== undefined) {
            body = (
              <HeldLine
                text={heldText}
                label={label}
                onUndo={() => {
                  // Every held row here was a "Use" (typing goes through the
                  // composer), so every Undo takes an adoption back.
                  trace("loom_answer_undo");
                  onUndo(field);
                }}
              />
            );
          } else if (sent.has(field)) {
            body = (
              <p className="focus-proposal-status" role="status">
                {t("readiness.answerSent")}
              </p>
            );
          } else if (row === undefined) {
            body = null;
          } else if (composerNamesField(composer, field)) {
            body = (
              <>
                <LoomBlock
                  reference
                  {...(row.kind === "list" ? { rows: row.rows ?? [] } : { text: row.text ?? "" })}
                />
                <p className="focus-proposal-status">
                  {t("focus.chat.proposal.writingBelow")}
                </p>
              </>
            );
          } else if (row.kind === "list") {
            body = (
              <LoomBlock rows={row.rows ?? []}>
                <div className="focus-suggest-actions">
                  <UseButton
                    label={t("focus.refine.suggest.adoptList")}
                    ariaLabel={t("focus.refine.suggest.adoptListAria", { label })}
                    onUse={() => {
                      trace("loom_answer_use");
                      onUse(field, row.sendText);
                    }}
                  />
                  <button
                    type="button"
                    className="focus-btn is-ghost"
                    aria-label={t("focus.refine.suggest.changeListAria", { label })}
                    disabled={composerLocked}
                    onClick={() => {
                      trace("loom_answer_change");
                      onChangeList(field, row.sendText);
                    }}
                  >
                    {t("focus.refine.suggest.changeList")}
                  </button>
                </div>
              </LoomBlock>
            );
          } else {
            body = (
              <LoomBlock text={row.text ?? ""}>
                <div className="focus-suggest-actions">
                  <UseButton
                    label={t("focus.refine.suggest.adopt")}
                    ariaLabel={t("focus.refine.suggest.adoptAria", { label })}
                    onUse={() => {
                      trace("loom_answer_use");
                      onUse(field, row.sendText);
                    }}
                  />
                  <button
                    type="button"
                    className="focus-btn is-ghost"
                    aria-label={t("focus.refine.suggest.writeOwnAria", { label })}
                    disabled={composerLocked}
                    onClick={() => {
                      trace("loom_answer_own");
                      onWriteOwn(field);
                    }}
                  >
                    {t("focus.refine.suggest.writeOwn")}
                  </button>
                </div>
              </LoomBlock>
            );
          }
          if (body === null) return null;
          return (
            <div key={field} className="focus-proposal-row">
              <p className="focus-proposal-field">{label}</p>
              {body}
            </div>
          );
        })}
      </div>
      {paused && (
        <p className="focus-proposal-note" role="status">
          {t("focus.chat.proposal.paused")}
        </p>
      )}
      {heldCount > 0 && (
        <div className="focus-proposal-bar" role="status">
          <span>{tn("plural.heldAnswers", heldCount)}</span>
          {busy && <span className="focus-note">{t("focus.chat.proposal.holdBusy")}</span>}
          {/* Only while the composer is empty: its own Send already carries
              the held answers, and "Send now" must never clear a paragraph
              the user has not sent. */}
          {composer.trim() === "" && (
            <button
              type="button"
              className="focus-btn is-ghost"
              onClick={onSendNow}
              disabled={busy || !canSendNow}
            >
              {t("readiness.sendNow")}
            </button>
          )}
        </div>
      )}
    </section>
  );
}
